/**
 * AttemptsSummaryCards — summary statistics above the attempts table.
 *
 * Manager / Admin see 3 cards: Total Attempts, Pass Rate, Average Score.
 * Candidate sees 1 card: "Your attempts: N".
 *
 * Matches sandbox reports.php summary-row layout.
 *
 * Issue 15-frontend-gaps/15.
 */

import type { ReactElement } from 'react';
import type { AttemptsTableRole } from './types.ts';

export interface AttemptsSummaryCardsProps {
  role: AttemptsTableRole;
  totalAttempts: number;
  passedCount: number;
  averageScore: number;
}

export function AttemptsSummaryCards({
  role,
  totalAttempts,
  passedCount,
  averageScore,
}: AttemptsSummaryCardsProps): ReactElement {
  const passRate =
    totalAttempts > 0
      ? Math.round((passedCount / totalAttempts) * 100)
      : 0;

  // Candidate sees a single card
  if (role === 'candidate') {
    return (
      <div className="mb-6 rounded-lg border border-line bg-white px-5 py-4">
        <div className="font-mono text-xs font-bold uppercase tracking-wider text-warning">
          Your attempts
        </div>
        <div className="mt-1 font-sans text-3xl font-medium tracking-tight">
          {totalAttempts}
        </div>
      </div>
    );
  }

  // Manager + Admin: 3 cards
  return (
    <div className="mb-6 grid grid-cols-3 gap-3">
      <SummaryCard label="Total Attempts" value={String(totalAttempts)} />
      <SummaryCard label="Pass Rate" value={`${passRate}%`} />
      <SummaryCard label="Average Score" value={`${averageScore.toFixed(1)}%`} />
    </div>
  );
}

function SummaryCard({
  label,
  value,
}: {
  label: string;
  value: string;
}): ReactElement {
  return (
    <div className="rounded-lg border border-line border-t-2 border-t-accent bg-white px-5 py-4">
      <div className="font-mono text-xs font-bold uppercase tracking-wider text-warning">
        {label}
      </div>
      <div className="mt-1 font-sans text-3xl font-medium tracking-tight">
        {value}
      </div>
    </div>
  );
}
